import { useState, useEffect, useRef } from "react";
import {
  IconBriefcase,
  IconCreditCard,
  IconUsers,
  IconCircleCheck,
  IconAlertCircle,
  IconStar,
  IconBell,
  IconArrowRight,
  IconChevronDown,
  IconX,
} from "@tabler/icons-react";
import styles from "./notificationDropdown.module.css";

// ── Icon config derived from API `type` field ───────────────────────────────
const TYPE_CONFIG = {
  JOB_APPLICATION: { Icon: IconBriefcase, bg: "#eff2ff", color: "#1d4ed8" },
  PAYMENT_SUCCESS: { Icon: IconCreditCard, bg: "#ecfdf5", color: "#059669" },
  MATCH_FOUND: { Icon: IconUsers, bg: "#fef3c7", color: "#d97706" },
  MATCH_CANCELLED: { Icon: IconAlertCircle, bg: "#fef2f2", color: "#dc2626" },
  SUBSCRIPTION_SUCCESS: {
    Icon: IconCircleCheck,
    bg: "#ecfdf5",
    color: "#059669",
  },
  SUBSCRIPTION_CANCELLED: {
    Icon: IconAlertCircle,
    bg: "#fef2f2",
    color: "#dc2626",
  },
  JOB_COMPLETED: { Icon: IconCircleCheck, bg: "#ecfdf5", color: "#059669" },
  FREE_TRIAL_EXPIRED: {
    Icon: IconAlertCircle,
    bg: "#fef2f2",
    color: "#dc2626",
  },
  WELCOME: { Icon: IconStar, bg: "#fef3c7", color: "#d97706" },
};
const DEFAULT_CONFIG = { Icon: IconBell, bg: "#f1f5f9", color: "#64748b" };

function getConfig(type) {
  return TYPE_CONFIG[type] || DEFAULT_CONFIG;
}

// ── Relative time formatter ─────────────────────────────────────────────────
function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── Single notification item ────────────────────────────────────────────────
function NotifItem({ notif, isExpanded, onNotifClick, onToggleExpand }) {
  const { Icon, bg, color } = getConfig(notif.type);
  const isUnread = !notif.isRead;

  return (
    <div
      className={[styles.notifItem, isUnread ? styles.notifItemUnread : ""]
        .filter(Boolean)
        .join(" ")}
      onClick={() => {
        onNotifClick(notif._id, null); // mark as read
        onToggleExpand(notif._id);
      }}
    >
      {/* Icon */}
      <div className={styles.notifIconWrap} style={{ background: bg, color }}>
        <Icon size={17} />
      </div>

      {/* Content */}
      <div className={styles.notifContent}>
        <div
          className={`${styles.notifItemTitle} ${isUnread ? styles.notifItemTitleUnread : ""}`}
        >
          {notif.title}
        </div>
        <div className={styles.notifItemBody}>{notif.message}</div>
        <div className={styles.notifItemTime}>
          {relativeTime(notif.createdAt)}
        </div>

        {/* Expandable action area */}
        <div
          className={`${styles.notifDetail} ${isExpanded ? styles.notifDetailOpen : ""}`}
        >
          {notif.actionUrl && (
            <a
              href={notif.actionUrl}
              className={styles.notifActionLink}
              onClick={(e) => e.stopPropagation()}
            >
              View details <IconArrowRight size={13} />
            </a>
          )}
        </div>
      </div>

      {/* Side — unread dot + chevron */}
      <div className={styles.notifItemSide}>
        {isUnread && <div className={styles.notifUnreadDot} />}
        <IconChevronDown
          size={14}
          className={`${styles.notifExpandIcon} ${isExpanded ? styles.notifExpandIconOpen : ""}`}
        />
      </div>
    </div>
  );
}

// ── Notification list ───────────────────────────────────────────────────────
function NotifList({
  notifications,
  expandedId,
  onNotifClick,
  onToggleExpand,
}) {
  return (
    <div className={styles.notifList}>
      {notifications.map((notif) => (
        <NotifItem
          key={notif._id}
          notif={notif}
          isExpanded={expandedId === notif._id}
          onNotifClick={onNotifClick}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </div>
  );
}

// ── Main exported component ─────────────────────────────────────────────────
export default function Notifications({
  notifications = [],
  isOpen,
  onClose,
  onMarkAllRead,
  onNotifClick,
  onSeeAll,
}) {
  const dropdownRef = useRef(null);
  const [expandedId, setExpandedId] = useState(null);

  // Toggle expanded — only one open at a time; clicking same one closes it
  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Close on outside click (desktop)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      const bell = document.querySelector('[aria-label="Notifications"]');
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        bell &&
        !bell.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [isOpen, onClose]);

  // Lock body scroll on mobile
  useEffect(() => {
    const mobile = window.innerWidth <= 768;
    document.body.style.overflow = isOpen && mobile ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Reset expanded when panel closes
  useEffect(() => {
    if (!isOpen) setExpandedId(null);
  }, [isOpen]);

  const isEmpty = notifications.length === 0;

  const listEl = isEmpty ? (
    <div className={styles.emptyState}>
      <IconBell size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
      <span>Your notification inbox is empty</span>
    </div>
  ) : (
    <NotifList
      notifications={notifications}
      expandedId={expandedId}
      onNotifClick={onNotifClick}
      onToggleExpand={handleToggleExpand}
    />
  );

  const footerEl = (
    <div className={styles.notifFooter}>
      <button className={styles.notifSeeAll} onClick={onSeeAll}>
        See all notifications <IconArrowRight size={13} />
      </button>
    </div>
  );

  return (
    <>
      {/* ── Desktop dropdown ── */}
      <div
        ref={dropdownRef}
        className={`${styles.notifDropdown} ${isOpen ? styles.notifDropdownOpen : ""}`}
        role="dialog"
        aria-label="Notifications panel"
      >
        <div className={styles.notifHeader}>
          <span className={styles.notifTitle}>Notifications</span>
          <button className={styles.notifMarkAll} onClick={onMarkAllRead}>
            Mark all as read
          </button>
        </div>
        {listEl}
        {footerEl}
      </div>

      {/* ── Mobile full-screen panel ── */}
      <div
        className={`${styles.notifMobile} ${isOpen ? styles.notifMobileOpen : ""}`}
        role="dialog"
        aria-label="Notifications panel"
      >
        <div className={styles.notifMobileHeader}>
          <span>Notifications</span>
          <div className={styles.notifMobileHeaderRight}>
            <button className={styles.notifMarkAll} onClick={onMarkAllRead}>
              Mark all as read
            </button>
            <button
              className={styles.notifMobileClose}
              aria-label="Close notifications"
              onClick={onClose}
            >
              <IconX size={18} />
            </button>
          </div>
        </div>
        <div className={styles.notifMobileBody}>
          {listEl}
          {footerEl}
        </div>
      </div>
    </>
  );
}
